import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/currency';

const API_URL =
  import.meta.env.SPOTLIGHT_VITE_API_URL || 'http://localhost:4001';

interface MetricItem {
  id: string;
  name?: string;
  type?: string;
  revenue?: number;
  employee_count?: number;
  // Additional fields for testimonials, case studies, awards
  company_name?: string;
  company_id?: string;
  author_name?: string;
  testimonial_text?: string;
  title?: string;
  description?: string;
  award_name?: string;
  awarding_body?: string;
  // Additional fields for opportunities
  opportunity_name?: string;
  pipeline_value?: number;
  status?: string;
  currency?: string;
}

interface ClickableMetricProps {
  value: number;
  label: string;
  icon?: React.ReactNode;
  color?: string;
  bgColor?: string;
  className?: string;
  organizationId: string;
  metricType:
    | 'prospects'
    | 'customers'
    | 'segments'
    | 'propositions'
    | 'companies'
    | 'testimonials'
    | 'case_studies'
    | 'awards'
    | 'pipeline_value'
    | 'closed_won_value';
  segmentId?: string;
  propositionId?: string;
  _positionId?: string;
  currency?: string;
}

export default function ClickableMetric({
  value,
  label,
  icon,
  color = 'text-gray-800',
  bgColor = 'bg-gray-50',
  className = '',
  organizationId,
  metricType,
  segmentId,
  propositionId,
  _positionId,
  currency = 'USD',
}: ClickableMetricProps) {
  const [showPopup, setShowPopup] = useState(false);
  const [items, setItems] = useState<MetricItem[]>([]);
  const [loading, setLoading] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopup]);

  const fetchItems = async () => {
    if (value === 0) return;

    setLoading(true);
    try {
      let url = `${API_URL}`;

      switch (metricType) {
        case 'prospects':
        case 'customers':
        case 'companies': {
          url += `/companies?organization_id=${organizationId}`;
          const response = await axios.get(url);
          let companies = response.data;

          // Filter by segment if provided
          if (segmentId && metricType !== 'companies') {
            companies = companies.filter((company: any) => {
              const hasSegment = company.segments?.some(
                (seg: any) => seg.id === segmentId,
              );
              return hasSegment;
            });
          }

          // Filter by both segment and proposition for position details
          if (segmentId && propositionId && metricType === 'companies') {
            companies = companies.filter((company: any) => {
              // Must have this segment
              const hasSegment = company.segments?.some(
                (seg: any) => seg.id === segmentId,
              );
              // For position details, we'll need to make additional API calls to check opportunities
              return hasSegment;
            });
          } else if (metricType === 'prospects' || metricType === 'customers') {
            // Filter companies based on their status field
            if (metricType === 'prospects') {
              companies = companies.filter((company: any) => {
                return company.status === 'prospect';
              });
            } else {
              companies = companies.filter((company: any) => {
                return company.status === 'customer';
              });
            }
          }

          setItems(companies);
          break;
        }

        case 'segments': {
          url += `/segments?organization_id=${organizationId}`;
          const segmentsResponse = await axios.get(url);
          setItems(segmentsResponse.data);
          break;
        }

        case 'propositions': {
          url += `/propositions?organization_id=${organizationId}`;
          const propsResponse = await axios.get(url);
          setItems(propsResponse.data);
          break;
        }

        case 'pipeline_value':
        case 'closed_won_value': {
          // Fetch companies and their opportunities
          url += `/companies?organization_id=${organizationId}`;
          const companiesResponse = await axios.get(url);
          let companies = companiesResponse.data;

          // Filter by segment if provided
          if (segmentId) {
            companies = companies.filter((company: any) =>
              company.segments?.some((seg: any) => seg.id === segmentId),
            );
          }

          // Fetch opportunities for each company
          const allOpportunities: any[] = [];
          for (const company of companies) {
            try {
              const oppResponse = await axios.get(
                `${API_URL}/companies/${company.id}/opportunities?organization_id=${organizationId}`,
              );

              // Filter opportunities based on type and proposition
              let opportunities = oppResponse.data;

              if (propositionId) {
                opportunities = opportunities.filter(
                  (opp: any) => opp.proposition_id === propositionId,
                );
              }

              if (metricType === 'pipeline_value') {
                opportunities = opportunities.filter(
                  (opp: any) => opp.status === 'Open',
                );
              } else {
                opportunities = opportunities.filter(
                  (opp: any) => opp.status === 'Won',
                );
              }

              // Add company info to each opportunity
              const oppsWithCompany = opportunities.map((opp: any) => ({
                id: opp.id,
                opportunity_name: opp.name,
                company_name: company.name,
                company_id: company.id,
                pipeline_value: opp.pipeline_value,
                status: opp.status,
                currency: currency,
              }));

              allOpportunities.push(...oppsWithCompany);
            } catch (error) {
              // Skip if error fetching opportunities
            }
          }

          setItems(allOpportunities);
          break;
        }

        case 'testimonials':
        case 'case_studies':
        case 'awards': {
          // First get all companies to find testimonials/case studies/awards
          url += `/companies?organization_id=${organizationId}`;
          const companiesResponse = await axios.get(url);
          let companies = companiesResponse.data;

          // Filter by segment if provided
          if (segmentId) {
            companies = companies.filter((company: any) =>
              company.segments?.some((seg: any) => seg.id === segmentId),
            );
          }

          // Fetch testimonials/case studies/awards for each company
          const allItems: any[] = [];
          for (const company of companies) {
            try {
              let itemsUrl = `${API_URL}/companies/${company.id}/`;
              if (metricType === 'testimonials') {
                itemsUrl += 'testimonials';
              } else if (metricType === 'case_studies') {
                itemsUrl += 'case-studies';
              } else {
                itemsUrl += 'awards';
              }
              itemsUrl += `?organization_id=${organizationId}`;

              const itemsResponse = await axios.get(itemsUrl);
              const itemsWithCompany = itemsResponse.data.map((item: any) => ({
                ...item,
                company_name: company.name,
                company_id: company.id,
              }));
              allItems.push(...itemsWithCompany);
            } catch (error) {
              // Skip if error fetching items for this company
            }
          }

          // Filter by proposition if provided
          if (propositionId) {
            const filteredItems = allItems.filter(
              (item: any) => item.proposition_id === propositionId,
            );
            setItems(filteredItems);
          } else {
            setItems(allItems);
          }
          break;
        }
      }
    } catch (error) {
      // console.error removed
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (value > 0) {
      setShowPopup(!showPopup);
      if (!showPopup && items.length === 0) {
        fetchItems();
      }
    }
  };

  const handleItemClick = (item: MetricItem) => {
    switch (metricType) {
      case 'prospects':
      case 'customers':
      case 'companies':
        navigate(`/companies/${item.id}`);
        break;
      case 'segments':
        navigate(`/segments/${item.id}`);
        break;
      case 'propositions':
        navigate(`/propositions/${item.id}`);
        break;
      case 'testimonials':
      case 'case_studies':
      case 'awards':
      case 'pipeline_value':
      case 'closed_won_value':
        // Navigate to the company that has this item
        navigate(`/companies/${item.company_id}`);
        break;
    }
    setShowPopup(false);
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`${bgColor} rounded-lg p-6 ${
          value > 0 ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''
        } ${className}`}
        onClick={handleClick}
      >
        <div className="flex items-center">
          {icon && (
            <div className="p-3 rounded-full bg-gray-100 mr-4">{icon}</div>
          )}
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>
              {metricType === 'pipeline_value' ||
              metricType === 'closed_won_value'
                ? formatCurrency(value, currency, { compact: true })
                : value}
            </p>
          </div>
        </div>
      </div>

      {showPopup && (
        <div
          ref={popupRef}
          className="absolute z-50 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-hidden"
        >
          <div className="sticky top-0 bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">{label} List</h3>
          </div>
          <div className="overflow-y-auto max-h-80">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : items.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No items found
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleItemClick(item)}
                  >
                    {metricType === 'testimonials' ? (
                      <>
                        <div className="font-medium text-gray-800">
                          {item.company_name}
                        </div>
                        {item.author_name && (
                          <div className="text-sm text-gray-600">
                            {item.author_name}
                          </div>
                        )}
                        {item.testimonial_text && (
                          <div className="text-sm text-gray-500 italic mt-1 line-clamp-2">
                            &ldquo;{item.testimonial_text}&rdquo;
                          </div>
                        )}
                      </>
                    ) : metricType === 'case_studies' ? (
                      <>
                        <div className="font-medium text-gray-800">
                          {item.title || 'Untitled Case Study'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.company_name}
                        </div>
                        {item.description && (
                          <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {item.description}
                          </div>
                        )}
                      </>
                    ) : metricType === 'awards' ? (
                      <>
                        <div className="font-medium text-gray-800">
                          {item.award_name || 'Untitled Award'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.company_name}
                        </div>
                        {item.awarding_body && (
                          <div className="text-sm text-gray-500">
                            {item.awarding_body}
                          </div>
                        )}
                      </>
                    ) : metricType === 'pipeline_value' ||
                      metricType === 'closed_won_value' ? (
                      <>
                        <div className="font-medium text-gray-800">
                          {item.opportunity_name || 'Untitled Opportunity'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {item.company_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: item.currency || 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(item.pipeline_value || 0)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-medium text-gray-800">
                          {item.name}
                        </div>
                        {item.type && (
                          <div className="text-sm text-gray-500">
                            {item.type}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
